import _ from 'lodash';
import autobind from 'autobind-decorator';
import {
	observable,
	action,
	reaction,
	computed,
	runInAction,
	extendObservable
} from 'mobx';

import { BaseStore, apiStore, songsStore, artistStore, userStore } from 'common/stores';
import config from 'client/shared/config';

const { routes } = config;

function removeBadChars (value) {
	return value
		.replace(/['|"|,|`]/g, '')
		.trim()
		.replace(/\s/g, '_');
}

class AlbumsStore extends BaseStore {
	apiRoute = routes.albums;

	@observable
	isSaving = true;

	constructor () {
		super();
		reaction(() => apiStore.data.albums, this.onNewData);
	}

	@computed
	get data () {
		return {
			album: this._data.item
		}
	}

	@computed
	get albums () {
		return this._items;
	}

	set albums (albums) {
		this._items = albums;
	}

	@computed
	get albumList () {
		const songs = songsStore.songs;

		if (_.isEmpty(songs) || _.isEmpty(this.albums)) {

			return [];
		}

		const albumList = this.albums.map((album) => {
			const { songs: albumSongs = [] } = album;
			return Object.assign({}, album, {
				songs: _(albumSongs)
					.map(songsStore.getSongByID.bind(songsStore))
					.compact()
					.value()
			});
		});

		return albumList;
	}

	@autobind
	@action
	onNewData (change) {
		this.albums = change;
		this.isLoading = false;
	}

	@action
	updateAlbum ({ key, value, _id, save = true}) {
		const route = save ? routes.album.replace(/\:_id/, `update/{_id}`) : null;
		const collectionKey = 'albums';
		this.updateCollectionItem({ key, value, _id, collectionKey, route });
	}

	@action
	async saveAlbumArtwork ({ album, imgData }) {
		const route = routes.albumArtworkUpload.replace(/\:_id/, album._id);
		const { artist } = artistStore.data;
		const { user } = userStore.data;

		const payload = {
			imgData: imgData,
			fileName: `album-artwork-${new Date().getTime()}`,
			path: `album-artwork/${user.userName}/${removeBadChars(artist.name)}/${album._id}`
		};

		const { data } = await this.ax.put(route, payload);

		runInAction('Update after saving album artowrk', () => {
			this.albums = this.albums.slice().map((album) => (
				album._id === data._id ? data : album
			));
		});
	}

	@action
	async saveAlbum ({ imgData = null }) {
		const { album } = this.data;
		const _id = album.get('_id');
		const route = _.isEmpty(_id) ? routes.createAlbum : routes.album.replace(/\:_id/, `update/${_id}`);
		const method = _.isEmpty(_id) ? 'post' : 'put';
		const payload = album.toPOJO();

		if (_.isNull(imgData) !== true) {

			const name = artistStore.data.artist.get('name');
			const userName = userStore.data.user.get('userName');
			const fileName = `album-artwork-${new Date().getTime()}`;
			const path = `album-artwork/${userName}/${removeBadChars(name)}`;

			Object.assign(payload, {
				fileName,
				imgData,
				path
			});
		}

		this.isSaving = true;
		const { data } = await this.ax[method](route, payload)
			.catch(({ message }) => console.error(message));

		runInAction('Update after saving album', () => {
			this.isSaving = false;

			this.data.album.replace(data);

			if (method === 'post') {

				this.albums.push(data);
			}
		});
	}

	@action
	setCurrent (_id) {
		if (_.isEmpty(_id)) {

			return;
		}

		this.data.album.replace(this.getItemByID({ _id }));
	}

	getAlbumByID (value) {
		// return this.getItemByID({ _id });
		if (_.isString(value)) {

			return _.first(this._items.filter(({ _id }) => _id === value));
		}

		if (_.isObject(value) && _.isArray(value.slice())) {

			return _(value.slice())
				.map((_id) => this.getAlbumByID(_id))
				.compact()
				.value();
		}
	}

	getAlbumArtwork (_id) {
		const album = this.getAlbumByID(_id);
		return _.isEmpty(album) ? null : `/assets/public/s3/img/${album.artwork}`;
	}

	getAlbumsForSong ({ _id }) {
		return this.albums.filter(({ songs = [] }) => songs.indexOf(_id) > -1);
	}
}

const albumsStore = new AlbumsStore();

export default albumsStore;
